import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface AgentVersion {
  id: string
  dateCreated: string
  dateUpdated: string
  agentSlug: string
  name: string
  versionNumber: number
  systemPrompt: string
  notes: string
  isActive: boolean
  createdBy: string | null
  createdByEmail: string | null
}

export interface AgentMcpAuditEntry {
  toolName: string
  timestamp: string
  userEmail: string
  orgIds: string[]
  authOk: boolean
  success: boolean
  latencyMs: number
  error: string
}

export interface ChatItem {
  id: string
  dateCreated: string
  isFromUser: boolean
  content: unknown
  metadata: Record<string, unknown> | null
  createdByEmail: string | null
}

export interface ChatConversation {
  id: string
  dateCreated: string
  title: string
  relatedModel: string
  relatedModelId: string
  items: ChatItem[]
  itemCount: number
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

function listAgentVersions(): Promise<AgentVersion[]> {
  return axios.get('/api/v1/insure/agent-versions/').then((r) => {
    const data = toCamelCase(r.data)
    const results = (data as { results?: AgentVersion[] }).results
    return (results ?? (data as AgentVersion[])) as AgentVersion[]
  })
}

function updateAgentVersion(id: string, data: Partial<AgentVersion>): Promise<AgentVersion> {
  return axios
    .patch(`/api/v1/insure/agent-versions/${id}/`, toSnakeCase(data))
    .then((r) => toCamelCase(r.data) as AgentVersion)
}

function createAgentVersion(
  data: Pick<AgentVersion, 'name' | 'systemPrompt' | 'notes' | 'agentSlug'>
): Promise<AgentVersion> {
  return axios
    .post('/api/v1/insure/agent-versions/', toSnakeCase(data))
    .then((r) => toCamelCase(r.data) as AgentVersion)
}

function activateAgentVersion(id: string): Promise<AgentVersion> {
  return axios
    .post(`/api/v1/insure/agent-versions/${id}/activate/`)
    .then((r) => toCamelCase(r.data) as AgentVersion)
}

function getMcpAuditLog(limit = 100): Promise<AgentMcpAuditEntry[]> {
  return axios
    .get(`/api/v1/insure/agent-versions/mcp-audit-log/?limit=${limit}`)
    .then((r) => (toCamelCase(r.data) as AgentMcpAuditEntry[]) ?? [])
}

function getChatHistory(limit = 50): Promise<ChatConversation[]> {
  return axios.get(`/api/v1/insure/agent-versions/chat-history/?limit=${limit}`).then((r) => {
    const data = toCamelCase(r.data)
    const results = (data as { results?: ChatConversation[] }).results
    return (results ?? (data as ChatConversation[])) as ChatConversation[]
  })
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export function useAgentVersions() {
  return useQuery({
    queryKey: [CacheKeys.AGENT_VERSIONS],
    queryFn: listAgentVersions,
  })
}

export function useCreateAgentVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createAgentVersion,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.AGENT_VERSIONS] }),
  })
}

export function useUpdateAgentVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<AgentVersion>) => updateAgentVersion(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.AGENT_VERSIONS] }),
  })
}

export function useActivateAgentVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: activateAgentVersion,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.AGENT_VERSIONS] }),
  })
}

export function useMcpAuditLog(limit = 100) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_MCP_AUDIT_LOG, limit],
    queryFn: () => getMcpAuditLog(limit),
    refetchInterval: 10_000,
  })
}

export function useChatHistory(limit = 50) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_CHAT_HISTORY, limit],
    queryFn: () => getChatHistory(limit),
  })
}

// ---------------------------------------------------------------------------
// Agent test stream
// ---------------------------------------------------------------------------

export type AgentTestStreamEvent =
  | {
      type: 'start'
      version: {
        id?: string
        name: string
        versionNumber?: number
        knowledgeBaseFiles: string[]
      }
    }
  | { type: 'delta'; delta: string }
  | { type: 'status'; status: string; tool?: string }
  | {
      type: 'final'
      response: string
      provider?: string
      sessionId?: string
      latencyMs?: number
      version: {
        id?: string
        name: string
        versionNumber?: number
        knowledgeBaseFiles: string[]
      }
    }
  | { type: 'error'; error: string }
  | {
      type: 'debug'
      sessionId?: string
      toolCalls?: Array<{
        toolName: string
        latencyMs: number
        success: boolean
        authOk: boolean
        userEmail?: string
        error?: string
        timestamp?: string
      }>
      latencyMs?: number
    }
  | { type: 'done' }

function buildStreamingHeaders(): Record<string, string> {
  return { Accept: 'text/event-stream, application/json, */*' }
}

function parseEventBlock(rawEvent: string): any {
  const data = rawEvent
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n')
    .trim()

  if (!data) {
    return null
  }

  try {
    return toCamelCase(JSON.parse(data))
  } catch {
    return null
  }
}

export async function streamTestAgent(
  payload: {
    message: string
    versionId?: string
    sessionId?: string
    chatHistory?: Array<{ role: string; content: string }>
    documentIds?: string[]
  },
  onEvent: (event: AgentTestStreamEvent) => void
): Promise<void> {
  const res = await axios.post('/api/v1/insure/agent-versions/test/stream/', toSnakeCase(payload), {
    headers: buildStreamingHeaders(),
    responseType: 'stream',
    adapter: 'fetch',
  })

  const stream = res.data as ReadableStream
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

    let boundaryIndex = buffer.indexOf('\n\n')
    while (boundaryIndex >= 0) {
      const rawEvent = buffer.slice(0, boundaryIndex)
      buffer = buffer.slice(boundaryIndex + 2)
      const event = parseEventBlock(rawEvent)
      if (event) {
        onEvent(event)
      }
      boundaryIndex = buffer.indexOf('\n\n')
    }

    if (done) {
      const trailingEvent = parseEventBlock(buffer)
      if (trailingEvent) {
        onEvent(trailingEvent)
      }
      return
    }
  }
}

// ---------------------------------------------------------------------------
// Evals stream
// ---------------------------------------------------------------------------

export type AgentEvalsStreamEvent =
  | { type: 'start' }
  | { type: 'output'; line: string }
  | { type: 'finished'; returnCode: number; success: boolean }
  | { type: 'error'; error: string }
  | { type: 'done' }

export async function streamEvals(onEvent: (event: AgentEvalsStreamEvent) => void): Promise<void> {
  const res = await axios.post(
    '/api/v1/insure/agent-versions/evals/stream/',
    {},
    {
      headers: buildStreamingHeaders(),
      responseType: 'stream',
      adapter: 'fetch',
    }
  )

  const stream = res.data as ReadableStream
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

    let boundaryIndex = buffer.indexOf('\n\n')
    while (boundaryIndex >= 0) {
      const rawEvent = buffer.slice(0, boundaryIndex)
      buffer = buffer.slice(boundaryIndex + 2)
      const event: AgentEvalsStreamEvent | null = parseEventBlock(rawEvent)
      if (event) {
        onEvent(event)
      }
      boundaryIndex = buffer.indexOf('\n\n')
    }

    if (done) {
      const trailingEvent: AgentEvalsStreamEvent | null = parseEventBlock(buffer)
      if (trailingEvent) {
        onEvent(trailingEvent)
      }
      return
    }
  }
}
